import { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import { PayPalScriptProvider, PayPalHostedFieldsProvider, PayPalHostedField, usePayPalHostedFields, PayPalButtons } from '@paypal/react-paypal-js';
import { WIX_INSTANCE_PARAMS } from '../../actions/component_actions';
import { WidgetLoader } from '../../loader/widget-loader';
import styles from './paypal-payment-form.scss';
import { StepButtonWrapper } from './checkout-stepper';
import { trackPaymentAttempt, trackPaymentResult } from '../../utils/journey-tracking';

const NotEligible = () => <div>Your account is not eligible. Make sure you have a business account and can accept payment</div>;

export function PayPalPaymentForm({
  children,
  ticketOverrides,
  settings,
  cart,
  form,
  eventId,
  onPaymentSubmit,
  promo_code,
  access_code,
  checkoutValues,
  session,
  onResizeComponent,
  event,
}) {
  const [clientToken, setClientToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const total = checkoutValues.cartTotal;

  useEffect(() => {
    if (+total === 0) return;
    (async () => {
      const { data } = await axios.post(
        `${process.env.EVENTVIEWER_SERVER}/api/v1/paypal/checkout/generate-client-id${WIX_INSTANCE_PARAMS}`,
        {},
        {
          withCredentials: true,
        }
      );
      setClientToken(data.client_token);
      setTimeout(() => {
        // give slight delay so resie can kick in. A hack!
        onResizeComponent();
        setLoading(false);
      }, 800);
    })();
  }, []);

  const handleOrder = async () => {
    // Prepare tracking data
    const eventData = {
      event_category: 'Ticket',
      event_label: event?.get('name'),
      currency: event?.get('currency'),
      payment_method: 'paypal',
      cart_id: session?.idempotency_key,
    };

    // Google Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'begin_payment', eventData);
    }

    // Facebook Pixel tracking
    if (window.fbq) {
      window.fbq('trackCustom', 'InitiatePayment', {
        content_name: event?.get('name'),
        content_category: 'Ticket',
        payment_method: 'paypal',
        currency: event?.get('currency'),
        value: checkoutValues?.total || 0,
      });
    }

    // Journey tracking for payment attempt
    if (event) {
      trackPaymentAttempt(event, { cart_id: session?.idempotency_key });
    }

    const { data } = await axios.post(`${process.env.EVENTVIEWER_SERVER}/api/v1/paypal/checkout/orders${WIX_INSTANCE_PARAMS}`, {
      cart,
      event_id: eventId,
      email: form.email,
      promo_code,
      access_code,
      ticket_overrides: ticketOverrides,
    });
    return data.id;
  };

  if (+total === 0) {
    return (
      <div className={styles.button}>
        {React.cloneElement(children, {
          onClick: () => onPaymentSubmit({ idempotency_key: session.idempotency_key }),
        })}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14, minHeight: session.payment.paypal.paypal_enable_credit_card_fields ? 200 : 65 }}>
      <WidgetLoader loading={loading} isRelative>
        <PayPalPayment
          handleOrder={handleOrder}
          clientToken={clientToken}
          onPaymentSubmit={onPaymentSubmit}
          buttonComponent={children}
          settings={settings}
          form={form}
          event={event}
          session={session}
          checkoutValues={checkoutValues}
        />
      </WidgetLoader>
    </div>
  );
}

const SubmitPayment = ({ buttonComponent, form, onPaymentSubmit, event }) => {
  const [processing, setProcessing] = useState(false);

  const { cardFields } = usePayPalHostedFields();
  const { first_name, last_name } = form;

  const handlePayment = async (ev) => {
    // const { first_name, last_name, email } = form;
    ev && ev.preventDefault();

    setProcessing(true);
    try {
      // if (typeof cardFields.submit === "function") return;
      const order = await cardFields.submit({
        // The full name as shown in the card and billing address
        cardholderName: `${first_name} ${last_name}`,
      });

      await onPaymentSubmit({
        bank_token: order.orderId,
        payment_type: 'paypal',
      });
      setProcessing(false);
    } catch (data) {
      const { details: [{ description } = {}] = [], message, response } = data;
      const result = description || response?.data || message;

      // Track payment error
      if (event) {
        trackPaymentResult(event, false, {
          error_message: result,
          payment_method: 'paypal',
        });
      }

      // Google Analytics tracking for payment error
      if (window.gtag) {
        window.gtag('event', 'payment_error', {
          event_category: 'Ticket',
          event_label: event?.get('name'),
          error_message: result,
          payment_method: 'paypal',
        });
      }

      // Facebook Pixel tracking for payment error
      if (window.fbq) {
        window.fbq('trackCustom', 'PaymentError', {
          content_name: event?.get('name'),
          content_category: 'Ticket',
          payment_method: 'paypal',
          error_message: result,
        });
      }

      await onPaymentSubmit({
        error: result,
      });
    }
  };

  return React.cloneElement(buttonComponent, {
    disabled: processing,
    className: processing ? 'button-saving' : '',
    onClick: handlePayment,
  });
};

function PayPalPayment({
  onResizeComponent,
  handleOrder,
  clientToken,
  onBack,
  onPaymentSubmit,
  buttonComponent,
  settings,
  form,
  event,
  session,
  checkoutValues,
}) {
  const isMobile = window.isMobileType;
  const customStyles = {
    border: '1px solid #606060',
    boxShadow: '2px 2px 10px 2px rgba(0,0,0,0.1)',
    height: 50,
    'border-radius': 8,
    background: 'white',
    color: 'black',
    padding: 5,
  };
  return (
    <PayPalScriptProvider
      options={{
        'client-id': process.env.PAYPAL_CLIENT_ID,
        components: 'hosted-fields,buttons',
        'data-client-token': clientToken,
        intent: 'capture',
        currency: window.currency || 'USD',
      }}
    >
      <PayPalButtons
        disabled={false}
        fundingSource="paypal"
        onInit={() => {
          onResizeComponent && onResizeComponent();
        }}
        createOrder={handleOrder}
        onApprove={async (order) => {
          // Journey tracking for successful payment
          if (event) {
            trackPaymentResult(event, true, {
              order_id: order.orderID,
              payment_method: 'paypal',
              cart_id: session?.idempotency_key,
            });
          }

          await onPaymentSubmit({
            bank_token: order.orderID,
            payment_type: 'paypal',
          });
        }}
      />
      {/* <PayPalButtons
            disabled={false}
            fundingSource="paylater"
            createOrder={handleOrder}
            onApprove={async (order) => {
              await onPaymentSubmit({
                bank_token: order.orderID,
                payment_type: 'paypal',
              });
            }}
          />

          <PayPalButtons
            disabled={false}
            fundingSource="venmo"
            createOrder={handleOrder}
            onApprove={async (order) => {
              await onPaymentSubmit({
                bank_token: order.orderID,
                payment_type: 'paypal',
              });
            }}
          /> */}

      {session.payment.paypal.paypal_enable_credit_card_fields && (
        <div className={styles.paypalCreditCard}>
          <PayPalHostedFieldsProvider notEligibleError={<NotEligible />} createOrder={handleOrder}>
            <Row className={isMobile ? styles.row : ''}>
              <Col xs={12}>
                <label className={`checkout-form-field ${styles.label}`} htmlFor="card-number">
                  <span className="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                    </svg>
                  </span>{' '}
                  {settings.get('cardNumberText')}
                </label>
                <PayPalHostedField
                  id="card-number"
                  className="card-field"
                  hostedFieldType="number"
                  style={customStyles}
                  options={{
                    selector: '#card-number',
                    placeholder: '1234-1234-1234-1234',
                  }}
                />
              </Col>
            </Row>
            <Row className={isMobile ? styles.row : ''} style={{ marginTop: 10 }}>
              <Col xs={isMobile ? '12' : '6'}>
                <label className={`checkout-form-field ${styles.label}`} htmlFor="card-expiry">
                  <span className="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                      <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                    </svg>
                  </span>
                  {settings.get('cardExpiryText')}
                </label>
                <PayPalHostedField
                  id="expiration-date"
                  className="card-field"
                  style={customStyles}
                  hostedFieldType="expirationDate"
                  options={{
                    selector: '#expiration-date',
                    placeholder: 'MM/YYYY',
                  }}
                />
              </Col>
              <Col xs={isMobile ? '12' : '6'}>
                <label className={`checkout-form-field ${styles.label}`} htmlFor="card-cvc">
                  <span className="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                  </span>

                  {settings.get('cardSecurityCodeText')}
                </label>
                <PayPalHostedField
                  id="cvv"
                  className="card-field"
                  hostedFieldType="cvv"
                  style={customStyles}
                  options={{
                    selector: '#cvv',
                    placeholder: '123',
                    maxlength: 4,
                  }}
                />
              </Col>
            </Row>
            <div className={styles.paymentButtonContainer}>
              <StepButtonWrapper>
                <Button variant="outline-dark" onClick={onBack} className="checkout-prev-button checkout-back-button">
                  {settings.get('checkoutBackButtonText')}
                </Button>
              </StepButtonWrapper>
              <SubmitPayment
                buttonComponent={buttonComponent}
                settings={settings}
                form={form}
                onPaymentSubmit={onPaymentSubmit}
                event={event}
                session={session}
                checkoutValues={checkoutValues}
              />
            </div>
          </PayPalHostedFieldsProvider>
        </div>
      )}
    </PayPalScriptProvider>
  );
}
